/**
 * iter302 — Shared Safety Tips card.
 *
 * Two variants:
 *   • `compact`  — used on Ad Details. Renders 3 tips + "View all safety tips"
 *                  link that opens the full list in a modal.
 *   • `full`     — used on My Account. Renders the 5 canonical tips. No link.
 *
 * Design rules (operator spec — iter302):
 *   • Soft amber/yellow warning card (not red, not scary).
 *   • Shield icon.
 *   • Friendly tone, not aggressive.
 *   • EN / AR with proper RTL bullet alignment.
 *   • Bottom-safe spacing so it never sits under the bottom nav or the
 *     orange + FAB.
 */
import React, { useState } from 'react';
import { ShieldCheck, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import './SafetyTipsCard.css';

// Canonical copy — DO NOT shorten / re-order without operator sign-off.
const TIPS_EN = [
  'Wassla does not handle payments or transactions between buyers and sellers.',
  'Do not send money before seeing the item in person.',
  'Meet in a safe public place whenever possible.',
  'Check the item carefully before paying.',
  'Be careful with advance payments, delivery deposits, or transfer requests.',
];
const TIPS_AR = [
  'وصلة لا تدير عمليات الدفع أو التحويلات بين البائع والمشتري.',
  'لا ترسل أي أموال قبل رؤية السلعة بنفسك.',
  'قابل البائع أو المشتري في مكان عام وآمن قدر الإمكان.',
  'افحص السلعة جيداً قبل الدفع.',
  'احذر من الدفع المقدم أو عربون التوصيل أو طلبات التحويل.',
];

export default function SafetyTipsCard({ variant = 'full', className = '' }) {
  const { lang } = useLanguage();
  const isArabic = lang === 'ar';
  const [showAll, setShowAll] = useState(false);
  const all = isArabic ? TIPS_AR : TIPS_EN;
  const compact = variant === 'compact';
  const shown = compact ? all.slice(0, 3) : all;

  return (
    <>
      <section
        className={`safety-card safety-card--${variant} ${className}`}
        dir={isArabic ? 'rtl' : 'ltr'}
        role="region"
        aria-label={isArabic ? 'نصائح السلامة' : 'Safety tips'}
        data-testid={`safety-card-${variant}`}
      >
        <div className="safety-card__head">
          <span className="safety-card__icon" aria-hidden="true">
            <ShieldCheck size={18} />
          </span>
          <h3 className="safety-card__title">
            {isArabic ? 'نصائح السلامة' : 'Safety Tips'}
          </h3>
        </div>
        <ul className="safety-card__list">
          {shown.map((t, i) => (
            <li key={i} data-testid={`safety-card-${variant}-tip-${i}`}>{t}</li>
          ))}
        </ul>
        {compact && all.length > shown.length && (
          <button
            type="button"
            className="safety-card__more"
            onClick={() => setShowAll(true)}
            data-testid="safety-card-view-all"
          >
            {isArabic ? 'عرض كل نصائح السلامة' : 'View all safety tips'}
          </button>
        )}
      </section>

      {compact && showAll && (
        <div
          className="safety-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label={isArabic ? 'نصائح السلامة' : 'Safety tips'}
          data-testid="safety-card-modal"
          onClick={() => setShowAll(false)}
        >
          <div
            className="safety-modal"
            onClick={(e) => e.stopPropagation()}
            dir={isArabic ? 'rtl' : 'ltr'}
          >
            <div className="safety-modal__head">
              <h3>
                <ShieldCheck size={18} style={{ verticalAlign: 'middle', marginInlineEnd: 8 }} />
                {isArabic ? 'نصائح السلامة' : 'Safety Tips'}
              </h3>
              <button type="button" onClick={() => setShowAll(false)} aria-label={isArabic ? 'إغلاق' : 'Close'}
                data-testid="safety-card-modal-close">
                <X size={18} />
              </button>
            </div>
            <ul className="safety-card__list">
              {all.map((t, i) => (
                <li key={i} data-testid={`safety-modal-tip-${i}`}>{t}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </>
  );
}
